/* global BigInt */
/**
 * WebSocket Service for Casino
 *
 * Connects to VPS WebSocket server with auth token.
 * Handles all game communication.
 */

const DIGITS_ONLY_RE = /[^0-9]/g;

class CasinoWebSocket {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    // 🔄 Auto-reconnect: No hard limit - keep trying forever with backoff
    this.maxReconnectDelay = 30000; // Max 30s between attempts
    this.baseReconnectDelay = 500; // Start at 500ms (faster reconnection)
    this.isConnected = false;
    this.isReconnecting = false; // 🔄 Track reconnection state for UI
    this.authToken = null;
    this.serverUrl = null;
    // Lobby support
    this.lobbyId = null;
    this.steamId = null;
    // 🛡️ FIX: Cache balance for bet validation (prevents precision loss on large numbers)
    this.cachedBalance = '0';
    // Cache init payload so late-mounting providers can self-hydrate
    this.cachedInitData = null;
    // 🎰 Balance update hold system - prevents spoiling game results during animations
    this._holdBalanceUpdates = false;
    this._pendingBalanceUpdate = null;
    // 🔄 Reconnection timer reference
    this._reconnectTimer = null;
    // 🔍 DEBUG: Store last close event to send to server on reconnect
    this._lastCloseEvent = null;
    // 🏥 Connection health check - detects "zombie" connections
    this._pingInterval = null;
    this._lastPong = Date.now();
    this._pingTimeout = null;
  }

  /**
   * 🛡️ Update cached balance and emit balanceUpdate event
   * This allows hooks to react to balance changes (e.g., auto-clamp bet amounts)
   * @param {string|number} newBalance - New balance in cents
   */
  updateCachedBalance(newBalance) {
    const newBalanceStr = String(newBalance);
    if (this.cachedBalance !== newBalanceStr) {
      this.cachedBalance = newBalanceStr;
      // 🎰 If balance updates are held (during game animations), store but don't emit
      if (this._holdBalanceUpdates) {
        this._pendingBalanceUpdate = newBalanceStr;
        return;
      }

      this.emit('balanceUpdate', newBalanceStr);

      // Broadcast to all connections so Razor HUD updates
      if (this.ws && this.ws.readyState === 1) {
        this.ws.send(JSON.stringify({ type: 'broadcastBalance' }));
      }
    }
  }

  /**
   * 🎰 Hold/release balance updates during game animations
   * When held, balance changes are cached but not emitted to the UI
   * When released, any pending balance update is emitted
   * @param {boolean} hold - true to hold, false to release
   */
  holdBalanceUpdates(hold) {
    this._holdBalanceUpdates = hold;

    // When releasing, emit any pending balance update
    if (!hold && this._pendingBalanceUpdate !== null) {
      this.emit('balanceUpdate', this._pendingBalanceUpdate);
      this._pendingBalanceUpdate = null;
    }

    // Broadcast balance to all connections (so Razor HUD updates after animation)
    if (!hold && this.ws && this.ws.readyState === 1) {
      this.ws.send(JSON.stringify({ type: 'broadcastBalance' }));
    }
  }

  /**
   * 🛡️ FIX: Validate and sanitize bet amount before sending to server
   * Prevents precision loss for numbers > Number.MAX_SAFE_INTEGER (9007199254740991)
   * Uses BigInt for precise comparison without any floating point errors
   * @param {string|number} betAmount - Bet amount in cents
   * @returns {string} Safe bet amount as string
   */
  validateBetAmount(betAmount) {
    try {
      // Convert to string and remove any non-numeric characters
      const betStr = String(betAmount).replace(DIGITS_ONLY_RE, '') || '0';
      const balStr = String(this.cachedBalance).replace(DIGITS_ONLY_RE, '') || '0';

      // Use BigInt for precise comparison (no precision loss for any size number)
      const betBig = BigInt(betStr);
      const balBig = BigInt(balStr);

      // If bet exceeds balance (could happen due to JS Number precision loss), cap to balance
      if (betBig > balBig && balBig > 0n) {
        return balStr;
      }

      // Minimum bet check ($1 = 100 cents)
      if (betBig < 100n) {
        return '100';
      }

      return betStr;
    } catch (e) {
      return '100'; // Safe fallback to minimum bet
    }
  }

  /**
   * Connect to WebSocket server with auth token
   */
  connect(serverUrl, authToken, options = {}) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    this.serverUrl = serverUrl;
    this.authToken = authToken;
    this.connectOptions = options; // Store for reconnect

    return new Promise((resolve, reject) => {
      try {
        // Connect to WebSocket server
        this.ws = new WebSocket(serverUrl);

        this.ws.onopen = () => {
          // 🔄 Don't set isConnected or emit events here - wait for 'init' message
          // The WebSocket is open but we're not authenticated yet
          // Keep isReconnecting true until we receive 'init' from server

          // 🚀 Register immediately with server using auth token
          // No delay needed - WebSocket is already in OPEN state
          const playerName = options.displayName || 'FALLBACK-TEST';
          try {
            const registerData = {
              clientType: 'react',
              displayName: playerName,
              previousClose: this._lastCloseEvent
            };
            const usingBootstrapTicket = Boolean(options.panelTicket) && authToken === options.panelTicket;
            if (!usingBootstrapTicket) registerData.token = authToken;
            const registerSteamId = options.steamId || this.steamId;
            if (registerSteamId) registerData.steamId = registerSteamId;
            if (usingBootstrapTicket) registerData.panelTicket = options.panelTicket;
            this.ws.send(JSON.stringify({
              type: 'register',
              data: registerData
            }));
            // Clear after sending
            this._lastCloseEvent = null;
          } catch (err) {
            // Send failed - onclose will handle reconnection
          }

          resolve();
        };

        this.ws.onclose = (event) => {
          this.isConnected = false;

          // 🔍 DEBUG: Store close event to send to server on reconnect
          this._lastCloseEvent = {
            code: event.code,
            reason: event.reason || '',
            wasClean: event.wasClean,
            timestamp: Date.now()
          };

          // 🔄 Auto-reconnect: Only emit disconnected for clean closes
          // For unexpected closes, emit 'reconnecting' and auto-reconnect
          if (event.code === 1000) {
            // Clean close (user initiated disconnect)
            this.isReconnecting = false;
            this.emit('disconnected', { code: event.code, reason: event.reason });
          } else {
            // Unexpected close - auto-reconnect silently
            this.isReconnecting = true;
            this.emit('reconnecting', {
              attempt: this.reconnectAttempts + 1,
              code: event.code,
              reason: event.reason
            });
            this.attemptReconnect();
          }
        };

        this.ws.onerror = (error) => {
          // 🔄 Don't emit error during reconnection - let it retry silently
          if (!this.isReconnecting) {
            this.emit('error', error);
          }
          reject(error);
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (err) {
            // Emit error for visibility
            this.emit('error', { message: 'Failed to parse server message', parseError: true });
          }
        };

      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Handle incoming messages from server
   */
  handleMessage(message) {
    switch (message.type) {
      case 'welcome':
        break;

      case 'pong':
        // 🏥 Server responded to our ping - connection is healthy
        this._lastPong = Date.now();
        if (this._pingTimeout) {
          clearTimeout(this._pingTimeout);
          this._pingTimeout = null;
        }
        break;

      case 'init':
        // 🔄 NOW we're truly connected and authenticated
        {
          const wasReconnecting = this.isReconnecting;
          this.isConnected = true;
          this.isReconnecting = false;
          this.reconnectAttempts = 0;

          // 🏥 Start connection health monitoring
          this._startPingInterval();

          // Emit connected/reconnected event now that auth succeeded
          if (wasReconnecting) {
            this.emit('reconnected', {});
          } else {
            this.emit('connected');
          }
        }

        this.steamId = message.steamId;
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        // Store reconnect token for seamless reconnection (1h TTL, survives server restarts)
        if (message.reconnectToken) {
          this.authToken = message.reconnectToken;
        }
        const initData = {
          steamId: message.steamId,
          balance: message.balance,
          name: message.name,
          avatarUrl: message.avatarUrl || null,
          bets: message.bets || [],
          isSpinning: message.isSpinning || false,
          recentBets: message.recentBets || [],
          // Level/Points system
          cash: message.cash,
          points: message.points,
          level: message.level,
          currentXp: message.currentXp,
          totalXp: message.totalXp,
          xpForNextLevel: message.xpForNextLevel,
          // Emotes for chat
          ownedEmotes: message.ownedEmotes || [],
          // Tutorial
          tutorialCompleted: message.tutorialCompleted || false,
        };
        this.cachedInitData = initData;
        this.emit('init', initData);
        break;

      case 'balance':
        // 🛡️ FIX: Cache balance on balance update
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('balance', { balance: message.balance });
        break;

      case 'levelUpdate':
        this.emit('levelUpdate', {
          level: message.level,
          currentXp: message.currentXp,
          totalXp: message.totalXp,
          xpAwarded: message.xpAwarded,
          xpSource: message.xpSource,
          xpForNextLevel: message.xpForNextLevel,
          levelUp: message.levelUp,
        });
        break;

      case 'levelUpBroadcast':
        this.emit('levelUpBroadcast', {
          steamId: message.steamId,
          playerName: message.playerName,
          newLevel: message.newLevel,
          newUnlocks: message.newUnlocks,
        });
        break;

      case 'bigWin':
        this.emit('bigWin', {
          name: message.name,
          amount: message.amount,
          game: message.game
        });
        break;

      case 'chat':
        this.emit('chat', {
          id: message.id || `${message.steamId}-${message.timestamp}`,
          steamId: message.steamId,
          username: message.name, // Map 'name' to 'username' for consistency
          avatarUrl: message.avatarUrl || null, // Steam avatar URL when available
          text: message.text,
          timestamp: message.timestamp,
          cosmetics: message.cosmetics || null, // { border, title, nameStyle, namePin }
          ownedEmotes: message.ownedEmotes || [], // Emotes the sender owns (for rendering)
          isSystem: message.isSystem || false,
          systemType: message.systemType || null,
          systemData: message.systemData || null,
        });
        break;

      case 'chatHistory':
        // Batch chat history received on connect/reconnect
        this.emit('chatHistory', {
          messages: message.messages || [],
        });
        break;

      case 'player-join':
        // Direct player join announcement (ephemeral, not in chat history)
        this.emit('playerJoin', {
          id: message.id,
          text: message.text,
          joinMessageStyle: message.joinMessageStyle,
          joinerName: message.joinerName,
          joinerAvatarUrl: message.joinerAvatarUrl,
          rarity: message.rarity || 'default',
          timestamp: message.timestamp,
          type: 'player-join',
        });
        break;

      case 'liveBets':
        this.emit('liveBets', {
          bets: message.bets || []
        });
        break;

      case 'betHistory':
        this.emit('betHistory', {
          tab: message.tab,
          bets: message.bets || []
        });
        break;

      case 'onlineCount':
        this.emit('onlineCount', {
          count: message.count || 0
        });
        break;

      case 'chatError':
        this.emit('chatError', {
          message: message.message || 'Chat error'
        });
        break;

      case 'chatCleared':
        this.emit('chatCleared', {
          message: message.message || 'Chat cleared'
        });
        break;

      case 'error':
        {
          const errorMsg = message.error || message.message || 'Unknown error';
          const lowerMsg = errorMsg.toLowerCase();

          // 🔑 Check for auth errors - auto-reconnect (server might just be restarting)
          const isAuthError = lowerMsg.includes('not authenticated') ||
                              lowerMsg.includes('authentication') ||
                              lowerMsg.includes('invalid') && lowerMsg.includes('token') ||
                              lowerMsg.includes('expired') && lowerMsg.includes('token');

          if (isAuthError) {
            // Auth failed - close and let onclose handler retry with backoff
            if (this.ws) {
              this.ws.close(4000, 'Auth failed');
            }
            return;
          }

          // 🔄 Don't emit errors during reconnection - let it retry silently
          if (!this.isReconnecting) {
            this.emit('error', { message: errorMsg });
          }
        }
        break;

      // Tower game events
      case 'towerGameStarted':
        // 🛡️ FIX: Cache balance on tower game started
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('towerGameStarted', {
          gameId: message.gameId,
          difficulty: message.difficulty,
          cols: message.cols,
          mines: message.mines,
          baseMultiplier: message.baseMultiplier,
          betAmount: message.betAmount,
          serverSeedHash: message.serverSeedHash,
          balance: message.balance,
          // Recovery fields
          currentRow: message.currentRow,
          selectedTiles: message.selectedTiles,
          status: message.status,
          recovered: message.recovered,
          currentMultiplier: message.currentMultiplier,
          nextMultiplier: message.nextMultiplier,
          potentialPayout: message.potentialPayout,
        });
        break;

      case 'towerTileResult':
        // 🛡️ FIX: Cache balance on tower tile result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('towerTileResult', {
          gameId: message.gameId,
          row: message.row,
          col: message.col,
          isMine: message.isMine,
          minePositions: message.minePositions,
          revealedMines: message.revealedMines,
          serverSeed: message.serverSeed,
          currentMultiplier: message.currentMultiplier,
          nextMultiplier: message.nextMultiplier,
          potentialPayout: message.potentialPayout,
          payout: message.payout,
          balance: message.balance,
          status: message.status,
        });
        break;

      case 'towerCashOut':
        // 🛡️ FIX: Cache balance on tower cash out
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('towerCashOut', {
          gameId: message.gameId,
          currentRow: message.currentRow,
          currentMultiplier: message.currentMultiplier,
          payout: message.payout,
          balance: message.balance,
          minePositions: message.minePositions,
          serverSeed: message.serverSeed,
          status: message.status,
        });
        break;

      case 'towerAutoResult':
        // 🛡️ FIX: Cache balance on tower auto result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('towerAutoResult', {
          gameId: message.gameId,
          difficulty: message.difficulty,
          betAmount: message.betAmount,
          path: message.path,
          hitMine: message.hitMine,
          hitMineRow: message.hitMineRow,
          finalRow: message.finalRow,
          minePositions: message.minePositions,
          serverSeed: message.serverSeed,
          serverSeedHash: message.serverSeedHash,
          clientSeed: message.clientSeed,
          multiplier: message.multiplier,
          payout: message.payout,
          balance: message.balance,
          status: message.status,
        });
        break;

      case 'towerError':
        this.emit('towerError', {
          error: message.error,
          gameId: message.gameId,
          timestamp: Date.now(),
        });
        break;

      // HiLo events
      case 'hiloStarted':
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('hiloStarted', {
          gameId: message.gameId,
          currentCard: message.currentCard,
          totalMultiplier: message.totalMultiplier,
          betAmount: message.betAmount,
          balance: message.balance,
          serverSeedHash: message.serverSeedHash,
          probabilities: message.probabilities,
          history: message.history,
          roundsPlayed: message.roundsPlayed,
          recovered: message.recovered,
        });
        break;

      case 'hiloGuessResult':
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('hiloGuessResult', {
          gameId: message.gameId,
          isWin: message.isWin,
          nextCard: message.nextCard,
          guess: message.guess,
          totalMultiplier: message.totalMultiplier,
          roundsPlayed: message.roundsPlayed,
          probabilities: message.probabilities,
          potentialPayout: message.potentialPayout,
          payout: message.payout,
          balance: message.balance,
          serverSeed: message.serverSeed,
        });
        break;

      case 'hiloSkipResult':
        this.emit('hiloSkipResult', {
          gameId: message.gameId,
          nextCard: message.nextCard,
          totalMultiplier: message.totalMultiplier,
          probabilities: message.probabilities,
        });
        break;

      case 'hiloCashedOut':
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('hiloCashedOut', {
          gameId: message.gameId,
          totalMultiplier: message.totalMultiplier,
          payout: message.payout,
          profit: message.profit,
          balance: message.balance,
          serverSeed: message.serverSeed,
          roundsPlayed: message.roundsPlayed,
        });
        break;

      case 'hiloError':
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('hiloError', {
          error: message.error,
          balance: message.balance,
        });
        break;


      // Mines events
      case 'minesGameStarted':
        // 🛡️ FIX: Cache balance on mines game started
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('minesGameStarted', {
          gameId: message.gameId,
          gridSize: message.gridSize,
          minesCount: message.minesCount,
          betAmount: message.betAmount,
          serverSeedHash: message.serverSeedHash,
          currentMultiplier: message.currentMultiplier,
          nextMultiplier: message.nextMultiplier,
          potentialPayout: message.potentialPayout,
          balance: message.balance,
          // Recovery fields
          revealedTiles: message.revealedTiles,
          status: message.status,
          recovered: message.recovered,
        });
        break;

      case 'minesTileResult':
        // 🛡️ FIX: Cache balance on mines tile result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('minesTileResult', {
          gameId: message.gameId,
          tileIndex: message.tileIndex,
          isMine: message.isMine,
          minePositions: message.minePositions,
          revealedTiles: message.revealedTiles,
          serverSeed: message.serverSeed,
          currentMultiplier: message.currentMultiplier,
          nextMultiplier: message.nextMultiplier,
          potentialPayout: message.potentialPayout,
          payout: message.payout,
          balance: message.balance,
          status: message.status,
        });
        break;

      case 'minesCashOut':
        // 🛡️ FIX: Cache balance on mines cash out
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('minesCashOut', {
          gameId: message.gameId,
          revealedTiles: message.revealedTiles,
          currentMultiplier: message.currentMultiplier,
          payout: message.payout,
          balance: message.balance,
          minePositions: message.minePositions,
          serverSeed: message.serverSeed,
          status: message.status,
        });
        break;

      case 'minesAutoResult':
        // 🛡️ FIX: Cache balance on mines auto result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('minesAutoResult', {
          gameId: message.gameId,
          gridSize: message.gridSize,
          minesCount: message.minesCount,
          betAmount: message.betAmount,
          tilesToReveal: message.tilesToReveal,
          revealedTiles: message.revealedTiles,
          hitMine: message.hitMine,
          hitMineIndex: message.hitMineIndex,
          minePositions: message.minePositions,
          serverSeed: message.serverSeed,
          serverSeedHash: message.serverSeedHash,
          clientSeed: message.clientSeed,
          multiplier: message.multiplier,
          payout: message.payout,
          balance: message.balance,
          status: message.status,
        });
        break;

      case 'minesError':
        this.emit('minesError', {
          error: message.error,
          gameId: message.gameId,
        });
        break;

      // Dice events
      case 'diceResult':
        // 🛡️ FIX: Cache balance on dice result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('diceResult', {
          gameId: message.gameId,
          result: message.result,
          target: message.target,
          rollOver: message.rollOver,
          isWin: message.isWin,
          multiplier: message.multiplier,
          winChance: message.winChance,
          payout: message.payout,
          profit: message.profit,
          betAmount: message.betAmount,
          serverSeed: message.serverSeed,
          serverSeedHash: message.serverSeedHash,
          clientSeed: message.clientSeed,
          balance: message.balance,
        });
        break;

      case 'diceError':
        this.emit('diceError', {
          error: message.error,
        });
        break;

      // Chicken Cross events
      case 'chickenState':
        // 🛡️ FIX: Cache balance on chicken state
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('chickenState', {
          gameId: message.gameId,
          difficulty: message.difficulty,
          currentLane: message.currentLane,
          currentMultiplier: message.currentMultiplier,
          nextMultiplier: message.nextMultiplier,
          potentialPayout: message.potentialPayout,
          betAmount: message.betAmount,
          serverSeedHash: message.serverSeedHash,
          balance: message.balance,
          status: message.status,
          recovered: message.recovered,
        });
        break;

      case 'chickenResult':
        // 🛡️ FIX: Cache balance on chicken result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('chickenResult', {
          gameId: message.gameId,
          result: message.result,
          currentLane: message.currentLane,
          currentMultiplier: message.currentMultiplier,
          nextMultiplier: message.nextMultiplier,
          potentialPayout: message.potentialPayout,
          payout: message.payout,
          balance: message.balance,
          serverSeed: message.serverSeed,
          clientSeed: message.clientSeed,
        });
        break;

      case 'chickenError':
        this.emit('chickenError', {
          error: message.error,
        });
        break;

      // Wheel events
      case 'wheelState':
        // 🛡️ FIX: Cache balance on wheel state
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('wheelState', {
          gameId: message.gameId,
          riskLevel: message.riskLevel,
          currentMultiplier: message.currentMultiplier,
          spinCount: message.spinCount,
          potentialPayout: message.potentialPayout,
          serverSeedHash: message.serverSeedHash,
          balance: message.balance,
          status: message.status,
          recovered: message.recovered,
          spinHistory: message.spinHistory || [],
          betAmount: message.betAmount,
        });
        break;

      case 'wheelSpinResult':
        // 🛡️ FIX: Cache balance on wheel spin result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('wheelSpinResult', {
          gameId: message.gameId,
          segmentIndex: message.segmentIndex,
          segmentMultiplier: message.segmentMultiplier,
          segmentColor: message.segmentColor,
          result: message.result,
          currentMultiplier: message.currentMultiplier,
          potentialPayout: message.potentialPayout,
          spinCount: message.spinCount,
          payout: message.payout,
          balance: message.balance,
          serverSeed: message.serverSeed,
          clientSeed: message.clientSeed,
          spinHistory: message.spinHistory || [],
          autoCashOut: message.autoCashOut,
          success: message.success,
          error: message.error,
        });
        break;

      case 'wheelCashOut':
        // 🛡️ FIX: Cache balance on wheel cash out
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('wheelCashOut', {
          gameId: message.gameId,
          finalMultiplier: message.finalMultiplier,
          payout: message.payout,
          balance: message.balance,
          serverSeed: message.serverSeed,
          clientSeed: message.clientSeed,
          spinHistory: message.spinHistory || [],
          status: message.status,
        });
        break;

      case 'wheelError':
        this.emit('wheelError', {
          error: message.error,
        });
        break;

      // Keno events
      case 'kenoResult':
        // 🛡️ FIX: Cache balance on keno result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('kenoResult', {
          gameId: message.gameId,
          selectedNumbers: message.selectedNumbers,
          drawnNumbers: message.drawnNumbers,
          hits: message.hits,
          hitsCount: message.hitsCount,
          selectionsCount: message.selectionsCount,
          riskLevel: message.riskLevel,
          multiplier: message.multiplier,
          payout: message.payout,
          balance: message.balance,
          serverSeed: message.serverSeed,
          serverSeedHash: message.serverSeedHash,
          clientSeed: message.clientSeed,
        });
        break;

      case 'kenoPayouts':
        this.emit('kenoPayouts', {
          riskLevel: message.riskLevel,
          payouts: message.payouts,
          totalNumbers: message.totalNumbers,
          drawCount: message.drawCount,
          minSelections: message.minSelections,
          maxSelections: message.maxSelections,
        });
        break;

      case 'kenoError':
        this.emit('kenoError', {
          error: message.error,
        });
        break;

      // Limbo events
      case 'limboResult':
        // 🛡️ FIX: Cache balance on limbo result
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('limboResult', {
          gameId: message.gameId,
          targetMultiplier: message.targetMultiplier,
          resultMultiplier: message.resultMultiplier,
          isWin: message.isWin,
          chance: message.chance,
          payout: message.payout,
          balance: message.balance,
          serverSeed: message.serverSeed,
          serverSeedHash: message.serverSeedHash,
          clientSeed: message.clientSeed,
        });
        break;

      case 'limboConfig':
        this.emit('limboConfig', {
          minMultiplier: message.minMultiplier,
          maxMultiplier: message.maxMultiplier,
          defaultMultiplier: message.defaultMultiplier,
          houseEdge: message.houseEdge,
        });
        break;

      case 'limboError':
        this.emit('limboError', {
          error: message.error,
        });
        break;

      // Plinko events
      case 'plinkoResult':
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('plinkoResult', {
          gameId: message.gameId,
          rows: message.rows,
          risk: message.risk,
          binIndex: message.binIndex,
          multiplier: message.multiplier,
          isWin: message.isWin,
          payout: message.payout,
          balance: message.balance,
          serverSeed: message.serverSeed,
          serverSeedHash: message.serverSeedHash,
          clientSeed: message.clientSeed,
        });
        break;

      case 'plinkoError':
        this.emit('plinkoError', { error: message.error });
        break;

      // Crash game events
      case 'crashRoundState':
        this.emit('crashRoundState', {
          roundId: message.roundId,
          state: message.state,
          crashPoint: message.crashPoint,
          players: message.players,
          serverSeedHash: message.serverSeedHash,
          countdown: message.countdown,
          elapsedMs: message.elapsedMs,
          currentMultiplier: message.currentMultiplier,
          history: message.history,
        });
        break;

      case 'crashMultiplier':
        this.emit('crashMultiplier', {
          roundId: message.roundId,
          multiplier: message.multiplier,
          elapsedMs: message.elapsedMs,
        });
        break;

      case 'crashPlayerJoined':
        this.emit('crashPlayerJoined', {
          roundId: message.roundId,
          player: message.player,
        });
        break;

      case 'crashPlayerCashedOut':
        this.emit('crashPlayerCashedOut', {
          roundId: message.roundId,
          steamId: message.steamId,
          displayName: message.displayName,
          avatarUrl: message.avatarUrl,
          multiplier: message.multiplier,
          payout: message.payout,
        });
        break;

      case 'crashRoundEnd':
        this.emit('crashRoundEnd', {
          roundId: message.roundId,
          crashPoint: message.crashPoint,
          serverSeed: message.serverSeed,
          players: message.players,
        });
        break;

      case 'crashBetConfirmed':
        // 🛡️ FIX: Cache balance on crash bet confirmed
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('crashBetConfirmed', {
          roundId: message.roundId,
          betAmount: message.betAmount,
          autoCashoutAt: message.autoCashoutAt,
          balance: message.balance,
          queued: message.queued,
        });
        break;

      case 'crashCashoutConfirmed':
        // 🛡️ FIX: Cache balance on crash cashout confirmed
        if (message.balance !== undefined) this.updateCachedBalance(message.balance);
        this.emit('crashCashoutConfirmed', {
          roundId: message.roundId,
          multiplier: message.multiplier,
          payout: message.payout,
          balance: message.balance,
        });
        break;

      case 'crashHistory':
        this.emit('crashHistory', {
          history: message.history,
        });
        break;

      case 'crashError':
        this.emit('crashError', {
          error: message.error,
        });
        break;

      // Achievement events
      case 'achievementsList':
        this.emit('achievementsList', {
          achievements: message.achievements,
          stats: message.stats,
        });
        break;

      case 'achievementsUnlocked':
        this.emit('achievementsUnlocked', {
          unlocked: message.unlocked,
          totalPointsAwarded: message.totalPointsAwarded,
        });
        break;

      case 'achievementsError':
        this.emit('achievementsError', {
          error: message.error,
        });
        break;

      case 'playerStats':
        this.emit('playerStats', {
          stats: message.stats,
        });
        break;

      case 'statsError':
        this.emit('statsError', {
          error: message.error,
        });
        break;

      // Shop events
      case 'shopState':
        this.emit('shopState', {
          points: message.points,
          items: message.items,
          equipped: message.equipped,
          stats: message.stats,
        });
        break;

      case 'shopPurchased':
        this.emit('shopPurchased', {
          itemId: message.itemId,
          itemName: message.itemName,
          price: message.price,
          newPoints: message.newPoints,
        });
        break;

      case 'shopEquipped':
        this.emit('shopEquipped', {
          itemId: message.itemId,
          category: message.category,
          equipped: message.equipped,
        });
        break;

      case 'shopUnequipped':
        this.emit('shopUnequipped', {
          category: message.category,
          equipped: message.equipped,
        });
        break;

      case 'shopError':
        this.emit('shopError', {
          error: message.error,
        });
        break;

      case 'prestigeState':
        this.emit('prestigeState', message);
        break;

      // Leaderboard events
      case 'leaderboard':
        this.emit('leaderboard', {
          type: message.leaderboardType,
          entries: message.entries,
          playerRank: message.playerRank,
          playerEntry: message.playerEntry,
        });
        break;

      case 'leaderboardsSummary':
        this.emit('leaderboardsSummary', {
          richest: message.richest,
          mostWagered: message.mostWagered,
          biggestWins: message.biggestWins,
          bestMultiplier: message.bestMultiplier,
          highestLevel: message.highestLevel,
          globalTotalWagered: message.globalTotalWagered || '0',
        });
        break;

      case 'leaderboardError':
        this.emit('leaderboardError', {
          error: message.error,
        });
        break;

      // Daily Reward events
      case 'dailyRewardState':
        this.emit('dailyRewardState', {
          canSpin: message.canSpin,
          nextSpinTime: message.nextSpinTime,
          timeUntilNextSpin: message.timeUntilNextSpin,
          totalSpins: message.totalSpins,
          totalPointsWon: message.totalPointsWon,
          currentStreak: message.currentStreak,
          bestStreak: message.bestStreak,
          segments: message.segments,
          spinDuration: message.spinDuration,
        });
        break;

      case 'dailyRewardSpinResult':
        this.emit('dailyRewardSpinResult', {
          success: message.success,
          segmentIndex: message.segmentIndex,
          pointsWon: message.pointsWon,
          newTotalPoints: message.newTotalPoints,
          newStreak: message.newStreak,
          bestStreak: message.bestStreak,
          totalSpins: message.totalSpins,
          totalPointsWon: message.totalPointsWon,
          nextSpinTime: message.nextSpinTime,
          spinDuration: message.spinDuration,
        });
        break;

      case 'dailyRewardError':
        this.emit('dailyRewardError', {
          error: message.error,
          nextSpinTime: message.nextSpinTime,
          timeUntilNextSpin: message.timeUntilNextSpin,
        });
        break;

      case 'favorites':
        this.emit('favorites', {
          favorites: message.favorites || [],
        });
        break;

      // Buff events
      case 'activeBuffs':
        this.emit('activeBuffs', {
          buffs: message.buffs || [],
        });
        break;

      case 'buffGranted':
        this.emit('buffGranted', {
          buff: message.buff,
          extended: message.extended || false,
          source: message.source,
        });
        break;

      case 'buffSummary':
        this.emit('buffSummary', {
          xpMultiplier: message.xpMultiplier,
          revenueMultiplier: message.revenueMultiplier,
          activeCount: message.activeCount,
        });
        break;

      case 'buffDefinitions':
        this.emit('buffDefinitions', {
          definitions: message.definitions || [],
        });
        break;

      case 'buffError':
        this.emit('buffError', {
          error: message.error,
        });
        break;

      case 'pendingBuffs':
        this.emit('pendingBuffs', {
          buffs: message.buffs || [],
        });
        break;

      case 'buffActivated':
        this.emit('buffActivated', {
          pendingBuffId: message.pendingBuffId,
          buff: message.buff,
          activeBuffs: message.activeBuffs || [],
        });
        break;

      case 'gameWins':
        this.emit('gameWins', {
          game: message.game,
          bigWins: message.bigWins || [],
          luckyWins: message.luckyWins || []
        });
        break;

      case 'stopAutoBet':
        window.dispatchEvent(new Event('stopAutoBet'));
        break;

      default:
        break;
    }
  }

  /**
   * Send message to server
   */
  send(message) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * Request current balance
   */
  getBalance() {
    return this.send({
      type: 'getBalance',
      data: { steamId: this.steamId }
    });
  }

  // ==========================================
  // TOWER GAME METHODS
  // ==========================================

  /**
   * Start a new Tower game
   * @param {string} difficulty - Easy, Medium, Hard, Expert, or Nightmare
   * @param {number} betAmount - Bet amount in cents
   * @param {string} clientSeed - Client seed for provably fair
   */
  towerStart(difficulty, betAmount, clientSeed) {
    return this.send({
      type: 'towerStart',
      // 🛡️ FIX: Validate bet amount
      data: { difficulty, betAmount: this.validateBetAmount(betAmount), clientSeed }
    });
  }

  /**
   * Pick a tile in the current Tower game
   * @param {string} gameId - The game ID
   * @param {number} col - Column index to pick (0-indexed)
   */
  towerPickTile(gameId, col) {
    return this.send({
      type: 'towerPickTile',
      data: { gameId, col }
    });
  }

  /**
   * Cash out of the current Tower game
   * @param {string} gameId - The game ID
   */
  towerCashOut(gameId) {
    return this.send({
      type: 'towerCashOut',
      data: { gameId }
    });
  }

  /**
   * Auto play a Tower game with a predefined path
   * @param {string} difficulty - Difficulty level
   * @param {number} betAmount - Bet amount in cents
   * @param {string} clientSeed - Client seed for provably fair
   * @param {number[]} path - Array of 8 column choices
   * @param {number} stopOnRow - Optional row to stop at (0-7)
   */
  towerAutoPlay(difficulty, betAmount, clientSeed, path, stopOnRow) {
    return this.send({
      type: 'towerAutoPlay',
      // 🛡️ FIX: Validate bet amount
      data: { difficulty, betAmount: this.validateBetAmount(betAmount), clientSeed, path, stopOnRow }
    });
  }

  // ==========================================
  // MINES GAME METHODS
  // ==========================================

  /**
   * Start a new Mines game
   * @param {number} betAmount - Bet amount in cents
   * @param {number} gridSize - Grid size (default 25 for 5x5)
   * @param {number} minesCount - Number of mines (1-24)
   * @param {string} clientSeed - Optional client seed for provably fair
   */
  minesStart(betAmount, gridSize = 25, minesCount = 5, clientSeed = null) {
    return this.send({
      type: 'minesStart',
      data: {
        // 🛡️ FIX: Validate bet amount
        betAmount: this.validateBetAmount(betAmount),
        gridSize,
        minesCount,
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  /**
   * Pick a tile in the current Mines game
   * @param {string} gameId - The game ID
   * @param {number} tileIndex - Tile index to reveal (0-24 for 5x5)
   */
  minesPickTile(gameId, tileIndex) {
    return this.send({
      type: 'minesPickTile',
      data: { gameId, tileIndex }
    });
  }

  /**
   * Cash out of the current Mines game
   * @param {string} gameId - The game ID
   */
  minesCashOut(gameId) {
    return this.send({
      type: 'minesCashOut',
      data: { gameId }
    });
  }

  /**
   * Auto play a Mines game with predefined tiles
   * @param {number} betAmount - Bet amount in cents
   * @param {number} gridSize - Grid size (default 25 for 5x5)
   * @param {number} minesCount - Number of mines
   * @param {number[]} tilesToReveal - Array of tile indices to reveal
   * @param {string} clientSeed - Optional client seed for provably fair
   */
  minesAutoPlay(betAmount, gridSize, minesCount, tilesToReveal, clientSeed = null) {
    return this.send({
      type: 'minesAutoPlay',
      data: {
        // 🛡️ FIX: Validate bet amount
        betAmount: this.validateBetAmount(betAmount),
        gridSize,
        minesCount,
        tilesToReveal,
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  // ==========================================
  // DICE GAME METHODS
  // ==========================================

  /**
   * Roll the dice
   * @param {number} betAmount - Bet amount in cents
   * @param {number} target - Target value (0.01 to 99.99)
   * @param {boolean} rollOver - true = roll over, false = roll under
   * @param {string} clientSeed - Client seed for provably fair
   */
  diceRoll(betAmount, target, rollOver, clientSeed = null) {
    return this.send({
      type: 'diceRoll',
      data: {
        // 🛡️ FIX: Validate bet amount
        betAmount: this.validateBetAmount(betAmount),
        target,
        rollOver,
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  // ==========================================
  // HILO GAME METHODS
  // ==========================================

  /**
   * Start a new HiLo game
   * @param {string} betAmount - Bet amount in cents (string for BigInt safety)
   * @param {string} clientSeed - Client seed for provably fair
   */
  hiloStart(betAmount, clientSeed) {
    return this.send({
      type: 'hiloStart',
      data: {
        betAmount: this.validateBetAmount(betAmount),
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  /**
   * Make a higher/lower guess in HiLo
   * @param {string} gameId - The game ID
   * @param {string} guess - 'hi' or 'lo'
   */
  hiloGuess(gameId, guess) {
    return this.send({
      type: 'hiloGuess',
      data: { gameId, guess }
    });
  }

  /**
   * Skip to a new card in HiLo (no multiplier change)
   * @param {string} gameId - The game ID
   */
  hiloSkip(gameId) {
    return this.send({
      type: 'hiloSkip',
      data: { gameId }
    });
  }

  /**
   * Cash out current HiLo winnings
   * @param {string} gameId - The game ID
   */
  hiloCashOut(gameId) {
    return this.send({
      type: 'hiloCashOut',
      data: { gameId }
    });
  }

  // ==========================================
  // PLINKO GAME METHODS
  // ==========================================

  /**
   * Play a Plinko round
   * @param {number} betAmount - Bet amount in cents
   * @param {number} rows - Number of rows (8-16)
   * @param {string} risk - Risk level (low/medium/high)
   * @param {string} clientSeed - Client seed for provably fair
   */
  plinkoPlay(betAmount, rows, risk, clientSeed = null) {
    return this.send({
      type: 'plinkoPlay',
      data: {
        betAmount: this.validateBetAmount(betAmount),
        rows,
        risk,
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  // ==========================================
  // CHICKEN CROSS GAME METHODS
  // ==========================================

  /**
   * Start a new Chicken Cross game
   * @param {number} betAmount - Bet amount in cents
   * @param {string} difficulty - Difficulty level (easy/medium/hard/daredevil)
   * @param {string} clientSeed - Client seed for provably fair
   */
  chickenStart(betAmount, difficulty, clientSeed = null) {
    return this.send({
      type: 'chickenStart',
      data: {
        // 🛡️ FIX: Validate bet amount
        betAmount: this.validateBetAmount(betAmount),
        difficulty,
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  /**
   * Move the chicken forward one lane
   * @param {string} gameId - The game ID
   */
  chickenMove(gameId) {
    return this.send({
      type: 'chickenMove',
      data: { gameId }
    });
  }

  /**
   * Cash out of the current Chicken Cross game
   * @param {string} gameId - The game ID
   */
  chickenCashOut(gameId) {
    return this.send({
      type: 'chickenCashOut',
      data: { gameId }
    });
  }

  // ==========================================
  // WHEEL GAME METHODS
  // ==========================================

  /**
   * Start a new Wheel game
   * @param {number} betAmount - Bet amount in cents
   * @param {string} riskLevel - Risk level (low/medium/high/risky)
   * @param {string} clientSeed - Client seed for provably fair
   */
  wheelStart(betAmount, riskLevel, clientSeed = null) {
    return this.send({
      type: 'wheelStart',
      data: {
        // 🛡️ FIX: Validate bet amount
        betAmount: this.validateBetAmount(betAmount),
        riskLevel,
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  /**
   * Spin the wheel
   * @param {string} gameId - The game ID
   */
  wheelSpin(gameId) {
    return this.send({
      type: 'wheelSpin',
      data: { gameId }
    });
  }

  /**
   * Cash out of the current Wheel game
   * @param {string} gameId - The game ID
   */
  wheelCashOut(gameId) {
    return this.send({
      type: 'wheelCashOut',
      data: { gameId }
    });
  }

  /**
   * Check for and recover active wheel game
   */
  wheelRecover() {
    return this.send({
      type: 'wheelRecover',
      data: {}
    });
  }

  // ==========================================
  // KENO GAME METHODS
  // ==========================================

  /**
   * Play a Keno game
   * @param {number} betAmount - Bet amount in cents
   * @param {string} riskLevel - Risk level (low/medium/high)
   * @param {number[]} selectedNumbers - Array of selected numbers (1-40)
   * @param {string} clientSeed - Client seed for provably fair
   */
  kenoPlay(betAmount, riskLevel, selectedNumbers, clientSeed = null) {
    return this.send({
      type: 'kenoPlay',
      data: {
        // 🛡️ FIX: Validate bet amount
        betAmount: this.validateBetAmount(betAmount),
        riskLevel,
        selectedNumbers,
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  /**
   * Get Keno payout table for a risk level
   * @param {string} riskLevel - Risk level (low/medium/high)
   */
  kenoGetPayouts(riskLevel) {
    return this.send({
      type: 'kenoGetPayouts',
      data: { riskLevel }
    });
  }

  // ==========================================
  // LIMBO GAME METHODS
  // ==========================================

  /**
   * Play a Limbo game
   * @param {number} betAmount - Bet amount in cents
   * @param {number} targetMultiplier - Target multiplier (1.01 to 1000000)
   * @param {string} clientSeed - Client seed for provably fair
   */
  limboPlay(betAmount, targetMultiplier, clientSeed = null) {
    return this.send({
      type: 'limboPlay',
      data: {
        // 🛡️ FIX: Validate bet amount
        betAmount: this.validateBetAmount(betAmount),
        targetMultiplier,
        clientSeed: clientSeed || Math.random().toString(36).substring(2, 18)
      }
    });
  }

  /**
   * Get Limbo game config
   */
  limboGetConfig() {
    return this.send({
      type: 'limboGetConfig',
      data: {}
    });
  }

  // ==========================================
  // CRASH GAME METHODS
  // ==========================================

  /**
   * Place a bet in the current Crash round
   * @param {number} betAmount - Bet amount in cents
   * @param {number|null} autoCashoutAt - Auto cashout multiplier (optional)
   */
  crashPlaceBet(betAmount, autoCashoutAt = null) {
    return this.send({
      type: 'crashPlaceBet',
      data: {
        // 🛡️ FIX: Validate bet amount
        betAmount: this.validateBetAmount(betAmount),
        autoCashoutAt
      }
    });
  }

  /**
   * Cash out of the current Crash round
   * @param {string} roundId - The round ID
   */
  crashCashout(roundId) {
    return this.send({
      type: 'crashCashout',
      data: { roundId }
    });
  }

  /**
   * Get current Crash game state
   */
  crashGetState() {
    return this.send({
      type: 'crashGetState',
      data: {}
    });
  }

  /**
   * Get Crash game history
   */
  crashGetHistory() {
    return this.send({
      type: 'crashGetHistory',
      data: {}
    });
  }

  /**
   * Subscribe to Crash game updates (call when entering crash page)
   * This enables receiving frequent multiplier updates
   */
  crashSubscribe() {
    return this.send({
      type: 'crashSubscribe',
      data: {}
    });
  }

  /**
   * Unsubscribe from Crash game updates (call when leaving crash page)
   * This stops receiving frequent multiplier updates
   */
  crashUnsubscribe() {
    return this.send({
      type: 'crashUnsubscribe',
      data: {}
    });
  }

  tutorialComplete() {
    return this.send({ type: 'tutorialComplete' });
  }

  sendChat(message) {
    return this.send({ type: 'chatSend', data: { message } });
  }

  // ==========================================
  // ACHIEVEMENTS METHODS
  // ==========================================

  /**
   * Get all achievements with progress
   */
  getAchievements() {
    return this.send({
      type: 'getAchievements',
      data: {}
    });
  }

  /**
   * Get player stats
   */
  getStats() {
    return this.send({
      type: 'getStats',
      data: {}
    });
  }

  /**
   * Get current Steam ID
   */
  getSteamId() {
    return this.steamId;
  }

  // ==========================================
  // DAILY REWARD METHODS
  // ==========================================

  /**
   * Get daily reward state
   */
  dailyRewardGetState() {
    return this.send({
      type: 'dailyRewardGetState',
      data: {}
    });
  }

  /**
   * Spin the daily reward wheel
   */
  dailyRewardSpin() {
    return this.send({
      type: 'dailyRewardSpin',
      data: {}
    });
  }

  // ==========================================
  // SHOP METHODS
  // ==========================================

  /**
   * Get shop state
   */
  shopGetState() {
    return this.send({
      type: 'shopGetState',
      data: {}
    });
  }

  /**
   * Request prestige/shop state from server
   */
  prestigeGetState() {
    return this.send({ type: 'prestigeGetState' });
  }

  /**
   * Purchase an item from the shop
   * @param {string} itemId - The item ID to purchase
   */
  shopPurchase(itemId) {
    return this.send({
      type: 'shopPurchase',
      data: { itemId }
    });
  }

  /**
   * Equip an item
   * @param {string} itemId - The item ID to equip
   */
  shopEquip(itemId) {
    return this.send({
      type: 'shopEquip',
      data: { itemId }
    });
  }

  /**
   * Unequip an item category
   * @param {string} category - The category to unequip
   */
  shopUnequip(category) {
    return this.send({
      type: 'shopUnequip',
      data: { category }
    });
  }

  // ==========================================
  // LEADERBOARD METHODS
  // ==========================================

  /**
   * Get a specific leaderboard
   * @param {string} type - Leaderboard type
   */
  getLeaderboardByType(type) {
    return this.send({
      type: 'getLeaderboard',
      data: { type }
    });
  }

  /**
   * Get leaderboards summary
   */
  getLeaderboardsSummary() {
    return this.send({
      type: 'getLeaderboardsSummary',
      data: {}
    });
  }

  // ==========================================
  // BUFF METHODS
  // ==========================================

  /**
   * Get active buffs for the player
   */
  getActiveBuffs() {
    return this.send({
      type: 'getActiveBuffs',
      data: {}
    });
  }

  /**
   * Get buff summary (multipliers)
   */
  getBuffSummary() {
    return this.send({
      type: 'getBuffSummary',
      data: {}
    });
  }

  /**
   * Get available buff definitions
   */
  getBuffDefinitions() {
    return this.send({
      type: 'getBuffDefinitions',
      data: {}
    });
  }

  // ==========================================
  // GAME WINS METHODS
  // ==========================================

  /**
   * Get big wins and lucky wins for a specific game
   * @param {string} game - Game identifier (keno, limbo, crash, mines, tower, dice, blackjack, roulette, wheel, chickencross)
   */
  getGameWins(game) {
    return this.send({
      type: 'getGameWins',
      data: { game }
    });
  }

  /**
   * Subscribe to events
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    // Replay cached init for late-mounting providers (e.g. RouletteProvider inside lazy routes)
    if (event === 'init' && this.cachedInitData) {
      try { callback(this.cachedInitData); } catch (e) {}
    }

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Emit event to listeners
   */
  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const snapshot = callbacks.slice();
      for (let i = 0; i < snapshot.length; i++) {
        try { snapshot[i](data); } catch (e) {}
      }
    }
  }

  /**
   * 🏥 Start connection health monitoring
   * Sends ping every 25s, expects pong within 5s
   * Detects "zombie" connections that appear open but are dead
   */
  _startPingInterval() {
    this._stopPingInterval(); // Clear any existing
    this._lastPong = Date.now();
    this._lastPingTick = Date.now();

    // Send ping every 25 seconds
    this._pingInterval = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this._stopPingInterval();
        return;
      }

      const now = Date.now();
      const timeSinceLastTick = now - this._lastPingTick;
      this._lastPingTick = now;

      // If the interval itself fired late (>32s instead of 25s), the JS main
      // thread was blocked (e.g. Three.js loading heavy 3D assets). Pongs may
      // have arrived over the network but onmessage couldn't run, so _lastPong
      // looks stale. Reset the timer instead of declaring a zombie connection.
      if (timeSinceLastTick > 32000) {
        this._lastPong = now;
        return;
      }

      // Check if last pong was too long ago (zombie detection)
      const timeSinceLastPong = now - this._lastPong;
      if (timeSinceLastPong > 35000) {
        // No pong for 35s - connection is dead, force reconnect
        this._stopPingInterval();
        if (this.ws) {
          this.ws.close(4000, 'Connection health check failed');
        }
        return;
      }

      // Send ping
      try {
        this.ws.send(JSON.stringify({ type: 'ping' }));

        // Set timeout for pong response (5 seconds)
        if (this._pingTimeout) clearTimeout(this._pingTimeout);
        this._pingTimeout = setTimeout(() => {
          // No pong received - connection might be dead
          // We'll catch it on the next ping cycle with the 35s check
        }, 5000);
      } catch (err) {
        // Send failed - connection is dead
        this._stopPingInterval();
        if (this.ws) {
          this.ws.close(4001, 'Ping send failed');
        }
      }
    }, 25000);
  }

  /**
   * 🏥 Stop connection health monitoring
   */
  _stopPingInterval() {
    if (this._pingInterval) {
      clearInterval(this._pingInterval);
      this._pingInterval = null;
    }
    if (this._pingTimeout) {
      clearTimeout(this._pingTimeout);
      this._pingTimeout = null;
    }
  }

  /**
   * Attempt to reconnect with exponential backoff
   */
  attemptReconnect() {
    this._stopPingInterval();

    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    this.reconnectAttempts++;
    this.isReconnecting = true;

    // Instant first attempt, then exponential backoff: 500ms, 1s, 2s, 4s, max 30s
    const delay = this.reconnectAttempts === 1 ? 0 : Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 2),
      this.maxReconnectDelay
    );

    this.emit('reconnecting', {
      attempt: this.reconnectAttempts,
      nextAttemptIn: delay,
      instant: delay === 0
    });

    this._reconnectTimer = setTimeout(() => {
      if (this.serverUrl && this.authToken) {
        this.connect(this.serverUrl, this.authToken, this.connectOptions || {})
          .catch(() => {
            // Will trigger another reconnect via onclose
          });
      } else {
        this.emit('reconnecting', {
          attempt: this.reconnectAttempts,
          error: 'No credentials available'
        });
        this._reconnectTimer = setTimeout(() => this.attemptReconnect(), delay);
      }
    }, delay);
  }

  /**
   * Disconnect
   */
  disconnect() {
    // 🏥 Stop health monitoring
    this._stopPingInterval();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.isConnected = false;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;

    // 🔄 Clear reconnection timer
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

  }
}

// Singleton instance
const casinoWS = new CasinoWebSocket();

export default casinoWS;
